import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogSlugs, ProtectedPaths } from "@/app/vars/Routes";

export const avatarValidationArticle: BlogArticle = {
  key: BlogArticleKey.AVATAR_VALIDATION,
  slug: BlogSlugs.AVATAR_VALIDATION,
  categoryId: "03",
  date: "2026-08-30",
  readTime: 7,
  locales: {
    fr: {
      title: "Savoir si ton avatar client est juste, et quand le couper en deux",
      excerpt:
        "Un avatar que personne n'a testé est une liste de souhaits. Trois tests pour savoir s'il tient, et le seul critère qui justifie d'en faire deux.",
      description:
        "Comment vérifier qu'un avatar client est utilisable, repérer qu'il est trop large, et savoir quand le séparer en deux plutôt que de l'élargir.",
      ctaTitle: "Vérifie ton avatar sur tes vrais appels",
      ctaText:
        "On construit ton avatar à partir de ce que tes clients ont dit, et on le corrige quand tes nouveaux appels le contredisent. Sans engagement.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Écrire un avatar client est facile. En écrire un qui sert à quelque chose l'est beaucoup moins, et rien ne distingue les deux à la lecture. Les deux tiennent sur une page, les deux ont l'air raisonnables.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La différence apparaît au moment de s'en servir : trier des profils, écrire un message, décider si une opportunité mérite du temps. Là, l'un tranche et l'autre se discute.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Un avatar qui ne permet d'exclure personne n'est pas un avatar, c'est une description de ton marché.",
        },
        { type: ArticleBlockType.H2, text: "Trois tests, dans cet ordre" },
        { type: ArticleBlockType.H3, text: "1. Peux-tu nommer cent entreprises ?" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Prends ton avatar et essaie d'écrire une liste de soixante-quinze à cent entreprises réelles qui y correspondent, avec un nom de personne à chaque fois. Pas des catégories, des noms.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Si tu bloques à quinze, il y a deux explications et il faut les distinguer. Soit ton avatar est trop étroit et ta cible n'existe qu'à quelques exemplaires, soit il est trop théorique et tu ne sais pas reconnaître les gens qu'il décrit. La deuxième est la plus fréquente, et la plus facile à corriger.",
        },
        { type: ArticleBlockType.H3, text: "2. Exclut-il quelqu'un ?" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Relis chaque ligne et demande-toi qui elle écarte. *Entreprise en croissance* n'écarte personne, tout le monde se dit en croissance. *Dirige une équipe commerciale de trois à quinze personnes* écarte immédiatement les équipes de un et celles de cinquante.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une ligne qui n'écarte personne ne coûte rien à écrire, et c'est exactement pour ça qu'elle se retrouve dans tous les avatars. Supprime-la, tu ne perdras rien.",
        },
        { type: ArticleBlockType.H3, text: "3. Quelqu'un d'autre trancherait-il pareil ?" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Prends dix profils, donne-les à quelqu'un avec ton avatar, sans autre explication, et demande-lui de dire oui ou non pour chacun. Si vos réponses divergent sur plus de deux, ce n'est pas la personne qui a mal lu, c'est que tes critères ne se tranchent pas.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ce test est le plus désagréable des trois et le plus utile. Il révèle que la moitié de ce que tu appelles un critère est en réalité une intuition que tu appliques sans t'en rendre compte.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Ce qui ne se tranche pas", "Ce qui se tranche"],
          rows: [
            ["Entreprise en croissance", "A recruté au moins deux personnes cette année"],
            ["Sensible à la performance commerciale", "Suit un objectif chiffré mensuel"],
            ["Structure moderne", "Utilise un CRM depuis plus d'un an"],
            ["A besoin de mieux vendre", "A perdu une affaire faute de suivi ce trimestre"],
          ],
        },
        { type: ArticleBlockType.H2, text: "Quand couper en deux, et quand ne pas le faire" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le réflexe, quand un avatar ne colle pas partout, est de l'élargir. C'est presque toujours la mauvaise réponse : un avatar élargi cesse d'exclure, et retombe dans le premier travers.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La bonne question n'est pas la taille, c'est de savoir si tu as affaire à un seul problème ou à deux.",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Ne coupe pas",
            items: [
              "Deux secteurs, le même problème",
              "Deux tailles d'entreprise, les mêmes objections",
              "Deux intitulés de poste, le même déclencheur",
              "Deux régions, le même vocabulaire",
            ],
          },
          good: {
            title: "Coupe en deux",
            items: [
              "Deux problèmes différents à l'origine de l'achat",
              "Des objections qui ne se ressemblent pas",
              "Des mots différents pour nommer la même chose",
              "Un déclencheur qui n'arrive pas au même moment",
            ],
          },
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le critère décisif est le vocabulaire. Si deux groupes de clients décrivent leur situation avec des mots différents, tu ne pourras jamais leur écrire le même message, et c'est bien la définition de deux avatars. S'ils emploient les mêmes mots, tout le reste est cosmétique.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Et garde-en peu. Deux avatars tenus à jour valent mieux que six qui vieillissent en silence, parce que chacun demande ses critères, ses messages et ses relances.",
        },
        { type: ArticleBlockType.H2, text: "Comment savoir qu'il a vieilli" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Un avatar juste ne le reste pas. Ton offre change, ton marché aussi, et ce qui déclenchait un achat il y a un an peut avoir disparu. Trois signaux, tous visibles dans tes appels.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Une objection nouvelle revient.** Elle n'était nulle part il y a six mois et elle tombe maintenant à chaque rendez-vous.",
            "**Le déclencheur a changé.** Les gens ne viennent plus pour la même raison, même s'ils achètent la même chose.",
            "**Tes meilleurs clients ne ressemblent plus à ta fiche.** Le signal le plus fort, et le plus facile à ignorer.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le troisième mérite une vraie vérification, une fois par trimestre : prends tes cinq derniers clients signés, confronte-les à ton avatar, et compte combien y entrent vraiment. En dessous de trois sur cinq, ta fiche décrit un client que tu ne vends plus.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Là où Meidly aide",
          text: "Ton avatar est extrait de tes appels plutôt qu'écrit de mémoire, donc il part de ce que les gens ont réellement dit. Il se met à jour quand de nouveaux appels arrivent, et un indicateur te signale quand il a pris du retard sur eux. Tes critères de ciblage et tes messages en découlent, et sont marqués comme à régénérer dès que l'avatar bouge.",
          linkLabel: "Découvrir l'avatar client",
          linkPath: ProtectedPaths.CLIENT_AVATAR,
        },
        { type: ArticleBlockType.H2, text: "En résumé" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Teste ton avatar avant de t'en servir : cent noms, des lignes qui excluent, et des critères qu'un tiers tranche comme toi. Ne l'élargis pas quand il coince, sépare-le, mais seulement si les mots employés diffèrent. Et reprends-le tous les trimestres sur tes derniers clients signés, parce que c'est eux qui disent s'il est encore juste.",
        },
      ],
      faq: [
        {
          question: "Comment savoir si son avatar client est bon ?",
          answer:
            "Trois tests. Peux-tu nommer soixante-quinze à cent entreprises réelles qui y correspondent, avec un nom de personne. Chaque ligne exclut-elle quelqu'un. Et quelqu'un d'autre, avec ta seule fiche, trierait-il dix profils comme toi. S'il échoue au troisième, tes critères sont des intuitions.",
        },
        {
          question: "Combien d'avatars clients faut-il avoir ?",
          answer:
            "Le moins possible, en général un ou deux. Chacun demande ses critères, ses messages et ses relances, donc six avatars signifie six ensembles qui vieillissent en silence. Deux tenus à jour valent mieux.",
        },
        {
          question: "Quand faut-il séparer un avatar en deux ?",
          answer:
            "Quand deux groupes de clients décrivent leur situation avec des mots différents, ou quand le déclencheur d'achat n'est pas le même. Deux secteurs ou deux tailles d'entreprise ne justifient rien si le problème et le vocabulaire sont identiques.",
        },
        {
          question: "Faut-il élargir un avatar qui ne colle pas partout ?",
          answer:
            "Non, c'est le piège le plus courant. Un avatar élargi cesse d'exclure, et un avatar qui n'exclut personne ne permet plus de trier ni d'écrire. Sépare plutôt, ou resserre.",
        },
        {
          question: "À quelle fréquence mettre à jour son avatar client ?",
          answer:
            "Une vérification par trimestre suffit : prends tes cinq derniers clients signés et compte combien entrent vraiment dans ta fiche. En dessous de trois sur cinq, elle décrit un client que tu ne vends plus.",
        },
      ],
    },
    en: {
      title: "Telling whether your client avatar holds, and when to split it in two",
      excerpt:
        "An avatar nobody tested is a wish list. Three tests to see whether yours holds, and the only criterion that justifies making it two.",
      description:
        "How to check that a client avatar is usable, spot when it is too broad or too theoretical, and know when to split it rather than widen it.",
      ctaTitle: "Check your avatar against your real calls",
      ctaText:
        "We build your avatar from what your customers actually said, and correct it when new calls contradict it. No strings attached.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Writing a client avatar is easy. Writing one that is any use is much harder, and nothing tells them apart on the page. Both fit on one sheet, both look reasonable.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The difference shows the moment you use it: sorting profiles, writing a message, deciding whether an opportunity deserves your time. There, one of them settles the question and the other invites debate.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "An avatar that lets you exclude nobody is not an avatar, it is a description of your market.",
        },
        { type: ArticleBlockType.H2, text: "Three tests, in this order" },
        { type: ArticleBlockType.H3, text: "1. Can you name a hundred companies?" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Take your avatar and try to write a list of seventy-five to a hundred real companies that match it, with a person's name each time. Not categories, names.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "If you stall at fifteen, there are two explanations and you need to tell them apart. Either the avatar is too narrow and your target exists in a handful of copies, or it is too theoretical and you cannot recognise the people it describes. The second is more common, and easier to fix.",
        },
        { type: ArticleBlockType.H3, text: "2. Does it exclude anyone?" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Read each line and ask who it rules out. *A growing company* rules out nobody, everyone describes themselves as growing. *Runs a sales team of three to fifteen* immediately rules out teams of one and teams of fifty.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A line that rules nobody out costs nothing to write, which is exactly why it ends up in every avatar. Delete it, you will lose nothing.",
        },
        { type: ArticleBlockType.H3, text: "3. Would someone else sort the same way?" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Take ten profiles, hand them to someone along with your avatar and no other explanation, and ask for a yes or no on each. If you disagree on more than two, it is not that they read badly, it is that your criteria cannot be settled.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "This is the most uncomfortable of the three and the most useful. It reveals that half of what you call a criterion is actually an instinct you apply without noticing.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["What cannot be settled", "What can"],
          rows: [
            ["A growing company", "Hired at least two people this year"],
            ["Cares about sales performance", "Tracks a monthly numeric target"],
            ["A modern outfit", "Has used a CRM for over a year"],
            ["Needs to sell better", "Lost a deal to poor follow-up this quarter"],
          ],
        },
        { type: ArticleBlockType.H2, text: "When to split, and when not to" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The reflex, when an avatar does not fit everywhere, is to widen it. That is almost always the wrong answer: a widened avatar stops excluding, and falls straight back into the first failure.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The right question is not size, it is whether you are facing one problem or two.",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Do not split",
            items: [
              "Two industries, the same problem",
              "Two company sizes, the same objections",
              "Two job titles, the same trigger",
              "Two regions, the same vocabulary",
            ],
          },
          good: {
            title: "Split in two",
            items: [
              "Two different problems behind the purchase",
              "Objections that look nothing alike",
              "Different words for naming the same thing",
              "A trigger that does not arrive at the same moment",
            ],
          },
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The deciding criterion is vocabulary. If two groups of customers describe their situation in different words, you will never write them the same message, and that is the definition of two avatars. If they use the same words, everything else is cosmetic.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "And keep few. Two avatars kept current beat six ageing quietly, because each one demands its own criteria, messages and follow-ups.",
        },
        { type: ArticleBlockType.H2, text: "How to tell it has aged" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A correct avatar does not stay correct. Your offer changes, so does your market, and what triggered a purchase a year ago may have gone. Three signals, all visible in your calls.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**A new objection recurs.** It was nowhere six months ago and now it lands in every meeting.",
            "**The trigger changed.** People no longer come for the same reason, even though they buy the same thing.",
            "**Your best customers no longer look like your sheet.** The strongest signal, and the easiest to ignore.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The third deserves a real check, once a quarter: take your last five signed customers, hold them against your avatar, and count how many genuinely fit. Below three out of five, your sheet describes a customer you no longer sell to.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Where Meidly helps",
          text: "Your avatar is extracted from your calls rather than written from memory, so it starts from what people actually said. It updates as new calls arrive, and a freshness marker tells you when it has fallen behind them. Your targeting criteria and your messages follow from it, and are flagged for regeneration as soon as the avatar moves.",
          linkLabel: "See how the client avatar works",
          linkPath: ProtectedPaths.CLIENT_AVATAR,
        },
        { type: ArticleBlockType.H2, text: "In short" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Test your avatar before using it: a hundred names, lines that exclude, and criteria a stranger settles the way you do. Do not widen it when it strains, split it, but only if the words differ. And revisit it every quarter against your last signed customers, because they are what says whether it still holds.",
        },
      ],
      faq: [
        {
          question: "How do you know if your client avatar is any good?",
          answer:
            "Three tests. Can you name seventy-five to a hundred real companies that match, with a person's name. Does each line exclude someone. And would someone else, given only your sheet, sort ten profiles the way you do. If it fails the third, your criteria are instincts.",
        },
        {
          question: "How many client avatars should you have?",
          answer:
            "As few as possible, usually one or two. Each demands its own criteria, messages and follow-ups, so six avatars means six sets ageing quietly. Two kept current are worth more.",
        },
        {
          question: "When should you split an avatar in two?",
          answer:
            "When two groups of customers describe their situation in different words, or when the buying trigger is not the same. Two industries or two company sizes justify nothing if the problem and the vocabulary are identical.",
        },
        {
          question: "Should you widen an avatar that does not fit everywhere?",
          answer:
            "No, that is the most common trap. A widened avatar stops excluding, and an avatar that excludes nobody no longer lets you sort or write. Split it instead, or tighten it.",
        },
        {
          question: "How often should a client avatar be updated?",
          answer:
            "A quarterly check is enough: take your last five signed customers and count how many genuinely fit the sheet. Below three out of five, it describes a customer you no longer sell to.",
        },
      ],
    },
  },
};
